import { useEffect, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import {
  FIFTY_MB_FILE_SIZE,
} from 'lib/constants';
import { useSnackbar } from 'notistack';
import { Media } from 'lib/types';
import { Add } from '@mui/icons-material';
import { LoadingOverlay } from 'components/LoadingOverlay';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { compact } from 'lodash';
import { getFileType, firstValidMediaUrl } from 'lib/helpers';

interface Props {
  onUpload: (m: any[]) => void;
  text?: string;
  hideReset?: boolean;
  onCancel?: () => void;
  skipPreview?: boolean;
  folder: string;
  maxSize?: number;
  initialPreviewUrl?: string;
  disabled?: boolean;
  disabledText?: string;
  /** By default, accept images */
  accept?: Accept;
  /**
   * The function that uploads the file via API.
   * It should return a promise that resolves to an array of Media objects.
   * TODO: Have the resume upload return media objects as well.
   */
  uploadFn: (fileType: string) => (
    file: FormData,
    filename: string,
    folder: string) => Promise<Media[]>;
  /** miniMode uses a smaller drag/drop zone without an icon and padding */
  miniMode?: boolean;
}

/**
 * FileUploadDragDrop can handle uploading any type of file.
 * Provide an uploadFn to handle the upload.
 */
export const FileUploadDragDrop = ({
  hideReset,
  text,
  onUpload,
  onCancel,
  skipPreview,
  folder,
  maxSize = FIFTY_MB_FILE_SIZE,
  initialPreviewUrl,
  disabled = false,
  disabledText,
  accept = { 'image/*': ['.jpeg', '.png', '.svg'] },
  miniMode = false,
  uploadFn,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [preview, setPreview] = useState(initialPreviewUrl);
  const [file, setFile] = useState<File | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setPreview(undefined);
    setFile(undefined);
    if (onCancel) onCancel();
  };

  useEffect(() => {
    if (initialPreviewUrl) {
      setPreview(initialPreviewUrl);
    }
  }, [initialPreviewUrl]);

  const uploadFile = async (files: File[]) => {
    try {
      setIsSaving(true);
      const fileData = new FormData();
      const firstFile = files?.[0];
      fileData.append('file', firstFile);
      setFile(firstFile);
      const apiUpload = uploadFn(firstFile.type);
      const uploadedMedia = await apiUpload(fileData, firstFile.name, folder);
      const firstMedia = firstValidMediaUrl(uploadedMedia);

      if (!skipPreview && firstMedia) {
        setPreview(firstMedia);
      }
      setIsSaving(false);
      onUpload(uploadedMedia);
    } catch (err) {
      setIsSaving(false);
      enqueueSnackbar(`Error uploading ${err}`, { variant: 'error' });
    }
  };

  const {
    getRootProps,
    getInputProps,
    fileRejections,
  } = useDropzone({
    accept,
    disabled: disabled || isSaving,
    maxFiles: 1,
    maxSize,
    onDropAccepted: uploadFile,
  });

  const rejectedFiles = () => {
    if (fileRejections.length === 0) return null;

    return (
      <ul className="text-red mb-4">
        <li>Some errors occurred:</li>
        {fileRejections.map((r) => (
          <li key={r.file.name}>
            {r.file.name}
            {' '}
            -
            {' '}
            {r.errors.map((e) => e.message)}
          </li>
        ))}
      </ul>
    );
  };

  const displayFile = [file?.name, file?.size ? `${(file.size / 1024).toFixed(2)} KB` : ''];

  if (miniMode) {
    return (
      <div {...getRootProps({ className: 'dropzone' })} className="cursor-pointer w-full">
        <LoadingOverlay open={isSaving} text="Uploading..." />
        <input {...getInputProps()} />
        <div className={disabled ? 'text-lightgray' : ''}>
          <p>{disabled && disabledText ? disabledText : (text || 'Click to upload')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative border bg-white border-dashed border-lightgray flex items-center justify-center text-center w-full p-4 mb-4">
        {(!hideReset && preview) && (
          <div className="absolute top-2 right-2">
            <IconButton onClick={reset}>
              <CloseIcon color="warning" />
            </IconButton>
          </div>
        )}
        <LoadingOverlay open={isSaving} text="Uploading..." />

        {!preview ? (
          <div {...getRootProps({ className: 'dropzone' })} className="cursor-pointer w-full">
            <input {...getInputProps()} />
            <div className={disabled ? 'text-lightgray' : ''}>
              <div className="mb-4"><Add /></div>
              <p>{disabled && disabledText ? disabledText : (text || 'Drop a file or click to upload')}</p>
            </div>
          </div>
        )
          : (
            <div>
              {getFileType(preview) === 'video' ? (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video src={preview} controls className="w-auto m-auto max-h-80" />
              ) : (
                <img
                  src={preview}
                  alt="preview"
                  className="w-auto m-auto max-h-80"
                />
              )}
              <div className="mt-4">
                {compact(displayFile).join(' - ')}
              </div>
            </div>
          )}
      </div>
      {rejectedFiles()}
    </>
  );
};
